import { apiFetch } from "@/lib/api";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Camera, ArrowLeft, History, Trash2, Languages, Clock,
  LogOut, ChevronDown, Loader2, Bookmark, BookmarkCheck
} from "lucide-react";


export default function HistoryPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await apiFetch(`/api/translations`, {
        
      });
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      setTranslations(data);
    } catch {
      toast.error("Failed to load translation history");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (translationId) => {
    try {
      const response = await apiFetch(`/api/translations/${translationId}`, {
        method: "DELETE",
        
      });
      if (!response.ok) throw new Error("Failed to delete");
      setTranslations((prev) => prev.filter((t) => t.translation_id !== translationId));
      toast.success("Translation deleted");
    } catch {
      toast.error("Failed to delete translation");
    }
  };

  const handleBookmark = async (translationId) => {
    try {
      const response = await apiFetch(`/api/translations/${translationId}/bookmark`, {
        method: "PUT",
        
      });
      if (!response.ok) throw new Error("Failed");
      const data = await response.json();
      setTranslations((prev) =>
        prev.map((t) =>
          t.translation_id === translationId ? { ...t, bookmarked: data.bookmarked } : t
        )
      );
      toast.success(data.bookmarked ? "Bookmarked!" : "Bookmark removed");
    } catch {
      toast.error("Failed to bookmark");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/dashboard")}>
            <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
              LingoLens
            </span>
          </div>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="ghost" className="rounded-full flex items-center gap-2 hover:bg-slate-50 px-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-indigo-100 text-indigo-700 text-sm font-bold">
                      {user?.name?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 rounded-xl">
                <DropdownMenuItem className="text-sm text-slate-500 cursor-default">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard-btn"
              variant="ghost"
              onClick={() => navigate("/dashboard")}
              className="rounded-full hover:bg-indigo-50"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Translation History
              </h1>
              <p className="text-slate-500 mt-1">{translations.length} translations</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : translations.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-3xl bg-indigo-50 flex items-center justify-center mx-auto mb-6">
              <History className="w-10 h-10 text-indigo-300" />
            </div>
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              No translations yet
            </h3>
            <p className="text-slate-400 mb-6">Your translation history will appear here</p>
            <Button
              data-testid="start-translating-btn"
              onClick={() => navigate("/dashboard")}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-3 font-bold"
            >
              Start Translating
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {translations.map((t, idx) => (
              <div
                key={t.translation_id}
                className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-indigo-100/10 p-6 hover:border-indigo-200 transition-all duration-200 animate-slide-up"
                style={{ animationDelay: `${idx * 50}ms` }}
                data-testid={`translation-card-${t.translation_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">
                        <Languages className="w-3 h-3" />
                        {t.target_language}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-slate-400 text-xs">
                        <Clock className="w-3 h-3" />
                        {formatDate(t.created_at)}
                      </span>
                    </div>
                    {/* Object tags */}
                    {t.translated_objects && t.translated_objects.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {t.translated_objects.map((obj, oi) => (
                          <span key={oi} className="inline-flex items-center gap-1 bg-pink-50 text-pink-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                            {obj.name} → {obj.translation}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {t.original_text && (
                        <div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Original</p>
                          <p className="text-sm text-slate-700 leading-relaxed line-clamp-3">{t.original_text}</p>
                        </div>
                      )}
                      {t.translated_text && (
                        <div>
                          <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-1">Translation</p>
                          <p className="text-sm text-slate-900 font-medium leading-relaxed line-clamp-3">{t.translated_text}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button
                      data-testid={`bookmark-${t.translation_id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleBookmark(t.translation_id)}
                      className={`rounded-full ${t.bookmarked ? "text-yellow-500 hover:text-yellow-600" : "text-slate-400 hover:text-yellow-500"}`}
                    >
                      {t.bookmarked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                    </Button>
                    <Button
                      data-testid={`delete-translation-${t.translation_id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(t.translation_id)}
                      className="text-slate-400 hover:text-red-500 rounded-full"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
